import React, { useEffect, useState } from "react";
import { useAuth } from "../../apis/auth/AuthProvider";
import { PERFIS_EMULAVEIS } from "../../services/emulacaoService";
import { getClients } from "../../services/clientService";
import { getUiInterface } from "../../locales/translationUtils";
import Client from "../../types/client";

/**
 * Seletores de emulação de perfil e de cliente, exibidos só para o ROLE_MASTER.
 *
 * A escolha vai para o servidor, que a grava no cookie — não é uma prévia visual.
 * O menu, as rotas e as respostas da API passam todos a obedecer ao perfil escolhido,
 * que é o único jeito de o master descobrir, por exemplo, que um ROLE_USER tomaria
 * 403 numa rota ou não enxergaria determinada carga.
 *
 * Enquanto a emulação está ligada o sistema fica somente leitura: quem bloqueia é o
 * back-end, e a faixa de aviso aqui existe para que ninguém descubra isso só ao tentar
 * salvar algo.
 */
const EmulacaoSeletores: React.FC = () => {
  const { podeEmular, emulacao, aplicaEmulacao, encerraEmulacaoAtual } = useAuth();

  const [clientes, setClientes] = useState<Client[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!podeEmular) return;

    let ativo = true;

    const carregaClientes = async () => {
      try {
        const resposta = await getClients();
        if (!ativo) return;

        // Ignora clientes sem nome — não dá pra escolher "em branco" no select
        // (dado ruim do dump de produção / registros de teste antigos).
        const lista = (resposta?.data?.values ?? []).filter(
          (cliente) => cliente?.id != null && (cliente.name ?? "").trim() !== ""
        );

        lista.sort((a, b) =>
          (a.name ?? "").localeCompare(b.name ?? "", "pt-BR", { sensitivity: "base" })
        );

        setClientes(lista);
      } catch {
        if (ativo) setClientes([]);
      }
    };

    carregaClientes();

    return () => {
      ativo = false;
    };
  }, [podeEmular]);

  if (!podeEmular) return null;

  const perfilSelecionado = emulacao?.perfilEmulado ?? "";
  const clienteSelecionado = emulacao?.clienteId != null ? String(emulacao.clienteId) : "";

  /**
   * Manda a escolha inteira ao servidor a cada mudança.
   *
   * Os dois seletores viajam juntos porque o endpoint substitui a emulação inteira:
   * enviar só o campo alterado apagaria silenciosamente o outro.
   */
  const troca = async (perfil: string, clienteId: string) => {
    setErro(null);
    setOcupado(true);

    try {
      if (!perfil && !clienteId) {
        await encerraEmulacaoAtual();
        return;
      }

      const cliente = clientes.find((c) => String(c.id) === clienteId);

      await aplicaEmulacao({
        perfil: perfil || null,
        clienteId: clienteId ? Number(clienteId) : null,
        clienteNome: cliente?.name ?? null,
      });
    } catch {
      setErro(getUiInterface("emulationError"));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="emulacao-bloco">
      <i className="bi bi-incognito" title={getUiInterface("emulationTitle")}></i>

      <label className="emulacao-campo">
        <span>{getUiInterface("emulationRole")}</span>
        <select
          value={perfilSelecionado}
          disabled={ocupado}
          onChange={(e) => troca(e.target.value, clienteSelecionado)}
        >
          <option value="">{getUiInterface("emulationOwnRole")}</option>
          {PERFIS_EMULAVEIS.map((perfil) => (
            <option key={perfil} value={perfil}>
              {perfil.replace("ROLE_", "")}
            </option>
          ))}
        </select>
      </label>

      <label className="emulacao-campo">
        <span>{getUiInterface("emulationClient")}</span>
        <select
          value={clienteSelecionado}
          disabled={ocupado}
          onChange={(e) => troca(perfilSelecionado, e.target.value)}
        >
          <option value="">{getUiInterface("emulationAllClients")}</option>
          {clientes.map((cliente) => (
            <option key={cliente.id} value={String(cliente.id)}>
              {cliente.name}
            </option>
          ))}
        </select>
      </label>

      {emulacao?.emulando && (
        <button
          type="button"
          className="emulacao-sair"
          disabled={ocupado}
          onClick={() => troca("", "")}
        >
          {getUiInterface("emulationExit")}
        </button>
      )}

      {erro && <span className="emulacao-erro">{erro}</span>}
    </div>
  );
};

export default EmulacaoSeletores;
